import { SmartSendColumn } from '../../Models/DataSources/SmartSend';

// Pure helpers that pick the DEFAULT business columns for the smart-send mapping screen.
// No React, no redux, no side effects — they are called from inside two smartSendSlice
// reducers (getMapping.fulfilled and loadSourceColumns.fulfilled), which is the only place
// where the column list and the three business ids land in the SAME commit.
//
// WHY THE SLICE AND NOT A useEffect IN THE SCREEN:
//  · a screen effect needs a "did I already apply this?" ref keyed on campaign+source, and that
//    key is wrong on a RETURN visit (A → B → A): selectSource has just nulled the three ids again
//    (smartSendSlice selectSource) but the key is already burnt, so the picker comes back empty.
//  · a reducer is structurally incapable of setting `dirty` — `dirty` is component state in
//    SmartSendScreen, written only by the two user onChange handlers. That is exactly the
//    property we need: a guessed default must NEVER trip the 750ms autosave and write a
//    synthetic group + a searchable-column index to the shared production DB on its own.
//    The default rides along on the first save the user genuinely triggers.

// NOTE ON THE SHORTFALL ("gap") COLUMN — there is deliberately NO auto-default for it.
//
// An earlier revision guessed it from the column name, porting the word list of the deployed
// supervisor job dbo.CampaignSupervisorJob_ProcessRequest_V2 (חוסר / נשאר / חסר) and adding
// פער / יעד because the columns in this project are named that way. Independent review killed it
// on two counts, both verified:
//
//  1. WRONG ANSWER. Scanning columns in Ordinal order and matching ANY word gives no priority
//     between words, so on this repo's own fixtures "יעד רבעוני" (Ordinal 4 — a TARGET column)
//     won over "פער מהיעד" (Ordinal 7 — the actual shortfall). The control labelled
//     "עמודת החוסר ליעד" would have pre-selected the target, and the next save would persist it.
//  2. REAL COST FOR A GUESS. A non-null GapColumnID makes CampaignsToDataSources_Set run the
//     OPENJSON SearchValues build and flip IsSearchable = 1 on the shared production version —
//     and DataSources_UpdateColumnMeta then returns -8 for turning IsSearchable back off while
//     the reference exists. A guessed value would be expensive AND awkward to undo.
//
// The product owner asked to DEFAULT the supervisor column and to MERGE the gap+sort controls;
// he did not ask to guess the shortfall column. So the merged picker starts empty, exactly as
// the two separate pickers did before. If a default is wanted later, align the word list with
// whatever ProcessRequest_V3 ends up detecting (V3 is not deployed today, so the mail is still
// ordered by what V2 finds in the mail BODY, not by this column).

// The three localized labels the upload wizard writes into DisplayName when it AUTO-detects a
// second email column as the supervisor (DataSources*.json → wizard.roleSupervisorEmail):
// he "אימייל מפקח" / en "Supervisor email" / pl "E-mail przełożonego".
export const SUPERVISOR_NAME_RE = /מפקח|supervisor|przełożon/i;

// "This column holds email addresses", by NAME. Used only by isEmailish (the send summary's
// warning); the default below never qualifies a column by its name.
const EMAIL_NAME_RE = /e-?mail|מייל|אימייל|דוא"?ל/i;

// DataSourceColumns.DataType: 1=Text, 2=Number, 3=Date, 4=Email, 5=Phone
const DATA_TYPE_EMAIL = 4;
// DataSourceColumns.SemanticRole: 0=None, 1=RecipientEmail, 2=RecipientCellphone
const SEMANTIC_ROLE_NONE = 0;
const SEMANTIC_ROLE_RECIPIENT_EMAIL = 1;

// THE RULE (Idan, 2026-10-11): «אם יש אימייל שני בקובץ - זה מזוהה כמפקח. השם הוא רק בונוס».
// A supervisor-email column has NO server-side marker: the wizard's IsSupervisorEmail flag is
// UI-only and is stripped before upload. What survives is the pair (DataType = EMAIL, not the
// recipient identity) — the upload wizard's own verdict "an email column that is not the
// identity", i.e. "a second email in the file":
//   · the wizard types a column EMAIL from its sampled values (columnTypeDetect, in
//     UploadWizardDialog buildColumns), from its header when every sampled cell is empty, or
//     because the operator tagged it supervisor ('sup', UploadWizardDialog.tsx:505); the server
//     stores the DataType it is sent (DataSources_Insert:291);
//   · on the wizard's auto path the first email column becomes the recipient identity, and a
//     version has at most one (SemanticRole = RecipientEmail, filtered unique index
//     IX_DataSourceColumns__VersionID_EmailRole) — so every other EMAIL column is a candidate.
// The NAME only ranks the candidates; it never makes a column one. The file's own header ranks
// first: the auto path writes the same label (he "אימייל מפקח") into the DisplayName of EVERY
// later email column, so between two of them only the header can tell.
//
// Why the name is not enough on its own (QA, Miri's file, 2026-10-11): the file had "מס מפקח"
// (numbers) before "אימייל מפקח" (emails). The previous first tier matched the NAME "מפקח" with no
// email check, first in Ordinal order, and offered the numbers. Saved, the V3 job would have sent
// the supervisor mail to the numbers (ProcessRequest_V3 keeps any non-empty cell).
//
// What the rule gives up: an email column whose DataType is not EMAIL is not offered. That happens,
// e.g., when the wizard demotes the previous identity to TEXT because another column took the
// email role (UploadWizardDialog.tsx:522-523), when the operator gives an email column another
// role, or when its sampled cells were not recognised as emails; EditColumnDialog offers no EMAIL
// for a non-identity column (EditColumnDialog.tsx:141). The picker lists every column, so the
// operator can still pick it.
const columnText = (c: SmartSendColumn) => `${c.DisplayName || ''} ${c.SourceHeader || ''}`;

// SUPERVISOR-PREDICATE-EXPORT: these two were file-private. The send-summary dialog needs the
// predicates the picker's default is built on, so that a column the auto-pick offers can never
// trip the pre-send warning. Re-deriving them there would create a second source of truth for one
// rule — which is exactly how the checkbox and the mapping drifted apart in the first place. The
// default is the stricter of the two (DataType = EMAIL ⊂ isEmailish); the warning still catches a
// column the OPERATOR picked by hand.

// Not the recipient identity — required of every candidate below.
export const isNotIdentity = (c: SmartSendColumn) => c.SemanticRole !== SEMANTIC_ROLE_RECIPIENT_EMAIL;

// Looks like it carries email addresses, by tag OR by name.
export const isEmailish = (c: SmartSendColumn) =>
    c.DataType === DATA_TYPE_EMAIL || EMAIL_NAME_RE.test(columnText(c));

/**
 * Default for the supervisor-email picker — THE RULE above.
 *
 * Candidates: the EMAIL columns that are not the recipient identity. Of those, the first whose
 * file header says "supervisor", else the first whose DisplayName says it (the wizard's label or
 * the operator's rename), else the first. No candidate ⇒ null, whatever the names say.
 *
 * Both server paths return columns ordered by Ordinal (CampaignsToDataSources_Get RS2 and
 * DataSources_Get RS2 both end in ORDER BY c.Ordinal ASC), so array order IS document order and
 * "the first" is the lowest Ordinal — the file's first column.
 */
export const pickDefaultSupervisorColumn = (columns: SmartSendColumn[] | null | undefined): number | null => {
    if (!columns || !columns.length) return null;
    const emails = columns.filter((c) => isNotIdentity(c) && c.DataType === DATA_TYPE_EMAIL);
    const says = (name: string | null | undefined) => SUPERVISOR_NAME_RE.test(name || '');
    const hit = emails.find((c) => says(c.SourceHeader)) || emails.find((c) => says(c.DisplayName)) || emails[0];
    return hit ? hit.ColumnID : null;
};

/**
 * Fills the supervisor column when — and ONLY when — there is nothing stored to respect.
 *
 * `applyDefaults` is the caller's answer to "is this a blank slate?". It MUST be false for a
 * mapping that already exists on the server, because "no supervisor" is a legitimate saved
 * decision: BusinessColumnsPicker maps the "ללא"/None item to null and the SP stores that NULL
 * verbatim, so a cleared value is indistinguishable from a never-chosen one by inspection.
 * Without that gate the guess is reinstated on every screen load and re-persisted by the next
 * save, and the user has no way to keep "no supervisor" on a source that has a second email
 * column. Callers pass `!IsMapped` for the load path and `true` for a fresh source pick.
 *
 * Gap and Sort are returned untouched — there is no auto-default for them; see the note above.
 */
export const applyBusinessColumnDefaults = (
    columns: SmartSendColumn[] | null | undefined,
    current: { supervisorColumnId: number | null; gapColumnId: number | null; sortColumnId: number | null },
    applyDefaults: boolean,
): { supervisorColumnId: number | null; gapColumnId: number | null; sortColumnId: number | null } => {
    const supervisorColumnId = (applyDefaults && current.supervisorColumnId == null)
        ? pickDefaultSupervisorColumn(columns)
        : current.supervisorColumnId;

    return { supervisorColumnId, gapColumnId: current.gapColumnId, sortColumnId: current.sortColumnId };
};
